import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { refreshAuthUser } from '~/features/auth/services/refresh-auth-user';
import { extractApiError } from '~/utils/extract-api-error';

import {
  bulkAddSubsidiaryUsers,
  createSubsidiary,
  deleteSubsidiary,
  getSubsidiaryHealth,
  listSubsidiaries,
  searchSubsidiaries,
  updateSubsidiary,
} from '../services/subsidiary.service';
import type {
  BulkAddSubsidiaryUserEntry,
  CreateSubsidiaryRequest,
  UpdateSubsidiaryRequest,
} from '../types/subsidiary.types';
import type { Tenant, TenantStatus } from '../types/tenant.types';

const SIXTY_SECONDS = 60 * 1000;

/* ------------------------------------------------------------------ */
/*  Queries                                                           */
/* ------------------------------------------------------------------ */

/**
 * Search subsidiaries (super admin). Pass `undefined` to fetch the full
 * accessible set — the service omits the `query` field in that case so the
 * backend returns every subsidiary instead of substring-matching the empty
 * string. Results are tagged with `type: 'SUBSIDIARY'`.
 */
export function useSubsidiarySearch(query: string | undefined) {
  return useQuery<Tenant[]>({
    queryKey: queryKeys.subsidiaries.search({ query: query ?? '' }),
    queryFn: async () => {
      const items = await searchSubsidiaries(query);
      return items.map((item) => ({ ...item, type: 'SUBSIDIARY' as const }));
    },
  });
}

/** List subsidiaries (current user scope). Results are tagged with `type: 'SUBSIDIARY'`. */
export function useSubsidiaryList() {
  return useQuery<Tenant[]>({
    queryKey: queryKeys.subsidiaries.list(),
    queryFn: async () => {
      const items = await listSubsidiaries();
      return items.map((item) => ({ ...item, type: 'SUBSIDIARY' as const }));
    },
  });
}

export function useSubsidiaryHealth(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.subsidiaries.health(),
    queryFn: getSubsidiaryHealth,
    staleTime: SIXTY_SECONDS,
    enabled,
  });
}

/* ------------------------------------------------------------------ */
/*  Mutations                                                          */
/* ------------------------------------------------------------------ */

/** Create a new subsidiary. POST /subsidiaries with { name, slug, organizationId }. */
export function useCreateSubsidiary() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateSubsidiaryRequest) => createSubsidiary(payload),
    onSuccess: () => {
      toast.success('Subsidiary created successfully.');
      void qc.invalidateQueries({ queryKey: queryKeys.subsidiaries.all });
      void qc.invalidateQueries({ queryKey: queryKeys.organizations.all });
      // Refresh auth store so TenantSwitcher picks up the new subsidiary.
      void refreshAuthUser();
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to create subsidiary'));
    },
  });
}

/** Update a subsidiary. Only name + status are updatable. */
export function useUpdateSubsidiary() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSubsidiaryRequest }) =>
      updateSubsidiary(id, payload),
    onSuccess: () => {
      toast.success('Subsidiary updated successfully.');
      void qc.invalidateQueries({ queryKey: queryKeys.subsidiaries.all });
      // Refresh auth store so TenantSwitcher reflects renamed/edited subsidiary.
      void refreshAuthUser();
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to update subsidiary'));
    },
  });
}

export function useUpdateSubsidiaryStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TenantStatus }) =>
      updateSubsidiary(id, { status }),
    onSuccess: () => {
      toast.success('Status updated.');
      void qc.invalidateQueries({ queryKey: queryKeys.subsidiaries.all });
      // Refresh auth store so TenantSwitcher reflects the new status.
      void refreshAuthUser();
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to update status'));
    },
  });
}

/**
 * Bulk-add existing platform users to a subsidiary. Called as a second step
 * after creating a subsidiary; failures are surfaced via toast but never block
 * the (already-succeeded) subsidiary creation.
 */
export function useBulkAddSubsidiaryUsers() {
  return useMutation({
    mutationFn: ({
      subsidiaryId,
      users,
    }: {
      subsidiaryId: string;
      users: BulkAddSubsidiaryUserEntry[];
    }) => bulkAddSubsidiaryUsers(subsidiaryId, { users }),
    onSuccess: () => {
      toast.success('Users added to subsidiary.');
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to add users to subsidiary'));
    },
  });
}

export function useDeleteSubsidiary() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteSubsidiary(id),
    onSuccess: () => {
      toast.success('Subsidiary deleted.');
      void qc.invalidateQueries({ queryKey: queryKeys.subsidiaries.all });
      // Refresh auth store so the deleted subsidiary disappears from the switcher.
      void refreshAuthUser();
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to delete subsidiary'));
    },
  });
}
